import { forwardRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiAlertCircle, FiAlertTriangle, FiArrowLeft, FiCheckCircle, FiEye, FiEyeOff, FiInfo } from "react-icons/fi";
import { ESTADOS } from "../lib/constants";
import { initials } from "../lib/format";

export function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Spinner({ className = "h-4 w-4" }) {
  return (
    <svg className={cx("animate-spin", className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const BUTTON_VARIANTS = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  ghost: "btn-ghost",
  danger: "btn-danger",
  white: "btn-white",
};

const BUTTON_SIZES = { sm: "btn-sm", md: "", lg: "btn-lg" };

export function Button({ variant = "primary", size = "md", loading, icon: Icon, to, className, children, ...props }) {
  const classes = cx("btn", BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className);
  const content = (
    <>
      {loading ? <Spinner /> : Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
      {children}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" className={classes} {...props} disabled={loading || props.disabled}>
      {content}
    </button>
  );
}

export function Container({ className, children }) {
  return <div className={cx("mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8", className)}>{children}</div>;
}

export function PageHeader({ title, description, actions, backTo }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {backTo && (
          <Link to={backTo} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 print:hidden">
            <FiArrowLeft className="h-4 w-4" /> Volver
          </Link>
        )}
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {description && <p className="mt-1.5 text-sm text-slate-500 sm:text-base">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ className, children }) {
  return <div className={cx("card", className)}>{children}</div>;
}

export function SectionCard({ step, icon: Icon, title, description, children, className }) {
  return (
    <section className={cx("card p-5 sm:p-6", className)}>
      <header className="mb-5 flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-sm font-bold text-brand-700 ring-1 ring-inset ring-brand-100">
          {Icon ? <Icon className="h-4 w-4" /> : step}
        </span>
        <div>
          <h2 className="font-semibold text-slate-900">{title}</h2>
          {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

export function Field({ label, htmlFor, hint, error, required, className, children }) {
  return (
    <div className={className}>
      {label && (
        <label htmlFor={htmlFor} className="label">
          {label}
          {required && <span className="text-rose-500"> *</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="mt-1.5 text-xs font-medium text-rose-600">{error}</p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>
      )}
    </div>
  );
}

export const Input = forwardRef(function Input({ className, invalid, ...props }, ref) {
  return <input ref={ref} className={cx("input", invalid && "input-error", className)} aria-invalid={invalid || undefined} {...props} />;
});

export const Select = forwardRef(function Select({ className, invalid, children, ...props }, ref) {
  return (
    <select ref={ref} className={cx("input", invalid && "input-error", className)} aria-invalid={invalid || undefined} {...props}>
      {children}
    </select>
  );
});

export const Textarea = forwardRef(function Textarea({ className, invalid, ...props }, ref) {
  return <textarea ref={ref} className={cx("input resize-y", invalid && "input-error", className)} {...props} />;
});

export function PasswordInput({ className, ...props }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={cx("relative", className)}>
      <Input type={visible ? "text" : "password"} className="pr-11" {...props} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 hover:text-slate-600"
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
      >
        {visible ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
      </button>
    </div>
  );
}

const BADGE_TONES = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  brand: "bg-brand-50 text-brand-700 ring-brand-100",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  sky: "bg-sky-50 text-sky-700 ring-sky-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
};

const DOT_TONES = {
  slate: "bg-slate-400",
  brand: "bg-brand-500",
  amber: "bg-amber-500",
  sky: "bg-sky-500",
  violet: "bg-violet-500",
  emerald: "bg-emerald-500",
  rose: "bg-rose-500",
};

export function Badge({ tone = "slate", dot, className, children }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset",
        BADGE_TONES[tone],
        className
      )}
    >
      {dot && <span className={cx("h-1.5 w-1.5 rounded-full", DOT_TONES[tone])} />}
      {children}
    </span>
  );
}

export function StatusBadge({ estado }) {
  const config = ESTADOS[estado] || { label: estado, tone: "slate" };
  return (
    <Badge tone={config.tone} dot>
      {config.label}
    </Badge>
  );
}

const ALERT_TONES = {
  error: { icon: FiAlertCircle, className: "border-rose-200 bg-rose-50 text-rose-800" },
  warning: { icon: FiAlertTriangle, className: "border-amber-200 bg-amber-50 text-amber-800" },
  success: { icon: FiCheckCircle, className: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  info: { icon: FiInfo, className: "border-brand-200 bg-brand-50 text-brand-800" },
};

export function Alert({ tone = "info", title, children, className }) {
  const { icon: Icon, className: toneClass } = ALERT_TONES[tone];
  return (
    <div role={tone === "error" ? "alert" : "status"} className={cx("flex gap-3 rounded-xl border p-3.5 text-sm", toneClass, className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? "mt-0.5 opacity-90" : undefined}>{children}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, className }) {
  return (
    <div className={cx("flex flex-col items-center px-6 py-12 text-center", className)}>
      {Icon && (
        <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
          <Icon className="h-6 w-6" />
        </span>
      )}
      <p className="font-semibold text-slate-900">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

const AVATAR_SIZES = { sm: "h-8 w-8 text-xs", md: "h-10 w-10 text-sm", lg: "h-16 w-16 text-xl" };

export function Avatar({ name, size = "md", className }) {
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-800 font-bold text-white",
        AVATAR_SIZES[size],
        className
      )}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

export function ProgressBar({ value }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-700 transition-all duration-300" style={{ width: `${value}%` }} />
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center text-brand-600">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

export function Modal({ open, onClose, children, className = "max-w-md p-6" }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div role="dialog" aria-modal="true" className={cx("relative w-full animate-fade-in rounded-2xl bg-white shadow-2xl", className)}>
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, title, description, confirmLabel = "Confirmar", tone = "danger", loading, onConfirm, onClose }) {
  return (
    <Modal open={open} onClose={loading ? () => {} : onClose}>
      <div className="flex gap-4">
        <span
          className={cx(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
            tone === "danger" ? "bg-rose-100 text-rose-600" : "bg-brand-100 text-brand-700"
          )}
        >
          <FiAlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-semibold text-slate-900">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant={tone === "danger" ? "danger" : "primary"} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
